// src/services/readinessService.js
import { db } from '../firebase';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';

/**
 * Checks workspace readiness based on real Firestore data
 * @param {string} problemId - The problem/job ID
 * @param {string} targetStatus - 'in-progress' or 'completed'
 * @param {string} currentUserId - The user attempting the transition
 * @returns {Promise<{isReady: boolean, missing: Array<string>, details: Object}>}
 */
export async function checkWorkspaceReadiness(problemId, targetStatus, currentUserId) {
  try {
    // Get the problem document
    const problemRef = doc(db, 'problems', problemId);
    const problemSnap = await getDoc(problemRef);

    if (!problemSnap.exists()) {
      return {
        isReady: false,
        missing: ['Problem not found'],
        details: {},
      };
    }

    const problemData = problemSnap.data();
    const isClient = problemData.clientId === currentUserId;
    const isHelper = problemData.helperId === currentUserId;

    // Security check: only assigned helper or client can change status
    if (!isClient && !isHelper) {
      return {
        isReady: false,
        missing: ['You are not authorized to change this job status'],
        details: {},
      };
    }

    // Get real data from Firestore
    const [filesSnap, notesSnap, messagesSnap] = await Promise.all([
      // Get files from files subcollection
      getDocs(collection(db, 'problems', problemId, 'files')),
      // Get notes from notes subcollection
      getDocs(collection(db, 'problems', problemId, 'notes')),
      // Get messages from messages subcollection
      getDocs(collection(db, 'problems', problemId, 'messages')),
    ]);

    // Count real user-generated content
    const fileCount = filesSnap.docs.length;
    const noteCount = notesSnap.docs.length;
    // Filter out system messages for message count
    const userMessages = messagesSnap.docs.filter(
      (doc) => doc.data().type !== 'system'
    );
    const messageCount = userMessages.length;

    // Also check if problem has attachment field (submission attachment)
    const hasSubmissionAttachment = Boolean(problemData.attachment);

    // Check if expert/helper has confirmed completion (for completed status)
    const expertSolved = Boolean(problemData.expertSolved);
    const clientSolved = Boolean(problemData.clientSolved);
    
    // Legacy support: also check old field names for backward compatibility
    const helperMarkedDone = Boolean(problemData.helperMarkedDone || problemData.expertSolved);
    const clientMarkedDone = Boolean(problemData.clientMarkedDone || problemData.clientSolved);

    // Build details object with real data
    const details = {
      fileCount,
      noteCount,
      messageCount,
      hasSubmissionAttachment,
      hasFiles: fileCount > 0 || hasSubmissionAttachment,
      hasNotes: noteCount > 0,
      hasMessages: messageCount > 0,
      expertSolved,
      clientSolved,
      helperMarkedDone, // Legacy support
      clientMarkedDone, // Legacy support
      isClient,
      isHelper,
    };

    // Check requirements based on target status
    const missing = [];

    if (targetStatus === 'in-progress') {
      // For in-progress: need at least one of files, notes, or messages
      if (!details.hasFiles && !details.hasNotes && !details.hasMessages) {
        missing.push('No files have been uploaded yet.');
        missing.push('No notes have been added.');
        missing.push('No chat messages exist yet.');
      }
    } else if (targetStatus === 'completed') {
      // For completed: ONLY mutual confirmation required (both sides must click "Solved")
      // No files/notes/messages requirement - if both agree, task is done
      
      // Mutual confirmation check - BOTH sides must confirm
      if (isClient) {
        // If client is marking solved, expert must have confirmed
        if (!expertSolved && !helperMarkedDone) {
          missing.push('The expert has not marked the task as solved yet.');
        }
      } else if (isHelper) {
        // If expert is marking solved, client must have confirmed
        if (!clientSolved && !clientMarkedDone) {
          missing.push('The client has not marked the task as solved yet.');
        }
      }
    }

    return {
      isReady: missing.length === 0,
      missing,
      details,
    };
  } catch (error) {
    console.error('Error checking workspace readiness:', error);
    return {
      isReady: false,
      missing: ['Error checking readiness: ' + error.message],
      details: {},
    };
  }
}

/**
 * Marks expert/helper confirmation for a job
 * @param {string} problemId - The problem/job ID
 * @param {string} helperId - The helper's user ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function markHelperDone(problemId, helperId) {
  try {
    const problemRef = doc(db, 'problems', problemId);
    const problemSnap = await getDoc(problemRef);

    if (!problemSnap.exists()) {
      return { success: false, error: 'Problem not found' };
    }

    const problemData = problemSnap.data();
    if (problemData.helperId !== helperId) {
      return { success: false, error: 'Not authorized' };
    }

    // Check if client has already confirmed
    const clientSolved = Boolean(problemData.clientSolved || problemData.clientMarkedDone);
    
    // Update with new schema
    const updateData = {
      expertSolved: true,
      updatedAt: serverTimestamp(),
    };

    // Set status based on whether client has confirmed
    if (clientSolved) {
      // Both confirmed - auto-complete will set status to "completed"
      // Don't set status here, let finalizeCompletionIfMutual handle it
    } else {
      // Only expert confirmed - waiting for client
      updateData.status = 'waiting_client_confirmation';
    }

    // Legacy support: also set old fields
    updateData.helperMarkedDone = true;
    updateData.helperMarkedDoneAt = serverTimestamp();
    updateData.helperMarkedDoneBy = helperId;

    await updateDoc(problemRef, updateData);

    return { success: true };
  } catch (error) {
    console.error('Error marking helper done:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Marks client confirmation for a job
 * @param {string} problemId - The problem/job ID
 * @param {string} clientId - The client's user ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function markClientDone(problemId, clientId) {
  try {
    const problemRef = doc(db, 'problems', problemId);
    const problemSnap = await getDoc(problemRef);

    if (!problemSnap.exists()) {
      return { success: false, error: 'Problem not found' };
    }

    const problemData = problemSnap.data();
    if (problemData.clientId !== clientId) {
      return { success: false, error: 'Not authorized' };
    }

    // Check if expert has already confirmed
    const expertSolved = Boolean(problemData.expertSolved || problemData.helperMarkedDone);
    
    // Update with new schema
    const updateData = {
      clientSolved: true,
      updatedAt: serverTimestamp(),
    };

    // Set status based on whether expert has confirmed
    if (expertSolved) {
      // Both confirmed - auto-complete will set status to "completed"
      // Don't set status here, let finalizeCompletionIfMutual handle it
    } else {
      // Only client confirmed - waiting for expert
      updateData.status = 'waiting_expert_confirmation';
    }

    // Legacy support: also set old fields
    updateData.clientMarkedDone = true;
    updateData.clientMarkedDoneAt = serverTimestamp();
    updateData.clientMarkedDoneBy = clientId;

    await updateDoc(problemRef, updateData);

    return { success: true };
  } catch (error) {
    console.error('Error marking client done:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Finalizes completion when BOTH client and expert have clicked "Solved".
 * Auto-completes the problem when both confirmations are present.
 * Does NOT change anything if mutual confirmation is not yet done.
 *
 * @param {string} problemId
 * @returns {Promise<{success: boolean, completed: boolean, problem?: any, error?: string}>}
 */
export async function finalizeCompletionIfMutual(problemId) {
  try {
    const problemRef = doc(db, 'problems', problemId);
    const problemSnap = await getDoc(problemRef);

    if (!problemSnap.exists()) {
      return { success: false, completed: false, error: 'Problem not found' };
    }

    const data = problemSnap.data();
    
    // Check new schema first, fallback to legacy
    const expertSolved = Boolean(data.expertSolved || data.helperMarkedDone);
    const clientSolved = Boolean(data.clientSolved || data.clientMarkedDone);

    // If either side hasn't confirmed yet, do nothing
    if (!expertSolved || !clientSolved) {
      return {
        success: true,
        completed: false,
        problem: { id: problemSnap.id, ...data },
      };
    }

    // If already completed, just return the current data
    if (data.status === 'completed') {
      return {
        success: true,
        completed: true,
        problem: { id: problemSnap.id, ...data },
      };
    }

    // Both confirmed and not yet completed -> mark as completed
    await updateDoc(problemRef, {
      status: 'completed',
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const updatedSnap = await getDoc(problemRef);

    return {
      success: true,
      completed: true,
      problem: { id: updatedSnap.id, ...updatedSnap.data() },
    };
  } catch (error) {
    console.error('Error finalizing mutual completion:', error);
    return {
      success: false,
      completed: false,
      error: error.message,
    };
  }
}

